import { PEEK_MS } from '../app/loop';
import { renderStep } from '../app/renderStep';
// The camera director: cuts between shots by what is happening, holds each shot long enough
// to read (no cut under MIN_SHOT_S unless the catnip trip demands it), and honours the viewer's
// choice of camera. A viewer who follows a cat is never pulled away from it.
// On a really tight finish it builds the tension (a duel shot, a slow push-in at the line) and
// then shows the finish video: a slow-motion replay of the crossing (drama.ts).
import type { PerspectiveCamera } from 'three';
import type { CatId, RaceEvent, RaceSnapshot } from '../race/types';
import { FinishReplay, type ReplayFrame } from './drama';
import { lerpVec, rig, runningLeader, type TripInfo } from './rigs';
import { type DirectorMemory, LANDING_CUT_S, PRIORITY, TRIP_MS, eventShot, rotationShot } from './shots';
import type { CameraChoice, CameraState, Shot, Vec3 } from './types';
import { v } from './types';

export const MIN_SHOT_S = 2.5;
const RECENT_MS = 6000;

export interface ViewerChoice {
  choice: CameraChoice;
  followed: CatId | null;
  /** Restrained effects: keep the camera level and still. */
  restrained?: boolean;
}

interface TripRecord { cat: CatId; lane: number; from: number; to: number | null; catchT: number }

export class Director {
  private trips: TripRecord[] = [];
  private landings: DirectorMemory['landings'] = [];
  private recent: RaceEvent[] = [];
  private hamShown = new Set<CatId>();
  private lastShown = { leadChange: -Infinity, ham: -Infinity };
  private current: Shot | null = null;
  private shotTime = 0;
  private rotationIndex = 0;
  private rotationSeconds = 0;
  private mode: CameraChoice = 'director';
  private raceNo = -1;
  private position: Vec3 = v(0, 5, 10);
  private target: Vec3 = v(0, 0, 0);
  private fov = 42;
  private anchor: Vec3 | null = null;
  private relPosition: Vec3 = v(0, 5, 10);
  private relTarget: Vec3 = v(0, 0, 0);
  private clock = 0;
  private lastT = Number.NaN;
  private finish = new FinishReplay();
  /** The last state, for the debug hook. */
  state: CameraState | null = null;
  /**
   * While the finish video runs: the race as it was, and how fast to play it. The world shows
   * this snapshot, with its animation time scaled by `rate`, instead of the live one.
   */
  replay: ReplayFrame | null = null;

  update(s: RaceSnapshot, events: RaceEvent[], peek: RaceEvent[], dt: number, viewer: ViewerChoice): CameraState {
    this.clock += dt;
    // Shot lengths run on render time, not on the loop's frame time: the loop caps dt at 0.1 s,
    // so on a slow frame rate (about 1 fps in headless replays) the gates shot would be held for
    // ten times its length while the race runs on. A jump in render time counts as at most 1 s.
    const shotDt = renderStep(this.lastT, s.t, dt);
    this.lastT = s.t;
    this.remember(s, events, peek);
    const trips = this.tripInfos(s);
    const memory: DirectorMemory = { trips, landings: this.landings, recent: this.recent, hamShown: this.hamShown, lastShown: this.lastShown };

    const viewerShot = this.viewerShot(s, viewer, trips);
    const modeChanged = viewer.choice !== this.mode;
    this.mode = viewer.choice;
    let next: Shot;
    if (viewerShot) next = viewerShot;
    else next = this.directorShot(s, peek, memory, modeChanged);
    this.replay = this.finish.update(s, events, shotDt, viewer.choice === 'director');
    const replay = this.replay;
    if (replay) next = { kind: 'photoFinish', subject: replay.cats[0], rival: replay.cats[1], priority: PRIORITY.finishReplay, reason: 'finish replay' };

    const cut = !this.current || next.kind !== this.current.kind || next.subject !== this.current.subject;
    if (cut) {
      this.shotTime = 0;
    } else {
      this.shotTime += shotDt;
    }
    this.current = next;
    if (next.reason === 'ham snack' && next.subject) { this.hamShown.add(next.subject); this.lastShown.ham = s.t; }
    if (next.reason === 'new leader') this.lastShown.leadChange = s.t;

    const scene = replay?.snapshot ?? s;
    const subject = next.subject ? scene.cats.find((c) => c.id === next.subject) ?? null : null;
    const rival = next.rival ? scene.cats.find((c) => c.id === next.rival) ?? null : null;
    const trip = replay ? null : trips.find((t) => t.cat === next.subject) ?? null;
    const push = replay ? replay.progress : next.reason === 'close finish' ? tightPush(s, peek) : 0;
    const pose = rig(next.kind, { snapshot: scene, subject, shotTime: this.shotTime, trip, rival, push });
    // Smooth relative to the shot's anchor, so the camera keeps up with a moving cat however
    // long the frame was; only the framing eases.
    const anchor = pose.anchor ?? v(0, 0, 0);
    const relPos = sub(pose.position, anchor);
    const relTarget = sub(pose.target, anchor);
    if (cut) {
      this.relPosition = relPos; this.relTarget = relTarget; this.fov = pose.fov;
    } else {
      const k = 1 - Math.exp(-pose.follow * dt);
      this.relPosition = lerpVec(this.relPosition, relPos, k);
      this.relTarget = lerpVec(this.relTarget, relTarget, k);
      this.fov += (pose.fov - this.fov) * k;
    }
    // A cat's position is already smooth, so follow it exactly; ease only group centres, which jump
    // when a cat joins or leaves the leading pack.
    const ease = pose.anchorEase ?? (subject ? 0 : 4);
    this.anchor = cut || !this.anchor || ease === 0 ? anchor : lerpVec(this.anchor, anchor, 1 - Math.exp(-ease * dt));
    this.position = add(this.anchor, this.relPosition);
    this.target = add(this.anchor, this.relTarget);
    const sway = viewer.restrained ? 0 : pose.handheld;
    const dist = Math.hypot(this.position.x - this.target.x, this.position.y - this.target.y, this.position.z - this.target.z);
    const a = dist * 0.006 * sway;
    const t = this.clock;
    const target = v(
      this.target.x + a * (Math.sin(t * 0.9) + 0.5 * Math.sin(t * 2.3 + 1)),
      this.target.y + a * 0.7 * (Math.sin(t * 1.3 + 2) + 0.4 * Math.sin(t * 3.1)),
      this.target.z + a * 0.5 * Math.sin(t * 0.7 + 4),
    );
    this.state = { position: { ...this.position }, target, fov: this.fov, shot: next.kind, subject: next.subject, reason: next.reason, mode: viewer.choice, cut, replay: !!replay };
    return this.state;
  }

  /** The shot the viewer asked for, or null when the director decides. */
  private viewerShot(s: RaceSnapshot, viewer: ViewerChoice, trips: TripInfo[]): Shot | null {
    const P = PRIORITY.rotation;
    switch (viewer.choice) {
      case 'director': return null;
      case 'overview': return { kind: 'overview', subject: null, priority: P, reason: 'viewer: overview' };
      case 'finish': return { kind: 'finishLine', subject: null, priority: P, reason: 'viewer: finish line' };
      case 'leader': {
        const l = runningLeader(s);
        return { kind: 'frontQuarter', subject: l?.id ?? null, priority: P, reason: 'viewer: leader' };
      }
      case 'follow': {
        const id = viewer.followed;
        if (!id) return null;
        const trip = trips.find((t) => t.cat === id);
        if (trip) {
          const kind = trip.elapsed >= LANDING_CUT_S ? 'landingHigh' : trip.elapsed < 2 ? 'catch' : 'tripFollow';
          return { kind, subject: id, priority: P, reason: 'viewer: following, on catnip' };
        }
        if (this.landings.some((l) => l.cat === id && s.t - l.t < 1500)) return { kind: 'landingHigh', subject: id, priority: P, reason: 'viewer: following, landed' };
        return { kind: 'chase', subject: id, priority: P, reason: 'viewer: following' };
      }
    }
  }

  private directorShot(s: RaceSnapshot, peek: RaceEvent[], memory: DirectorMemory, modeChanged: boolean): Shot {
    const want = eventShot(s, peek, memory);
    const cur = this.current;
    const held = this.shotTime >= MIN_SHOT_S;
    const takeRotation = (advance: boolean): Shot => {
      if (advance) this.rotationIndex++;
      const r = rotationShot(s, this.rotationIndex);
      this.rotationSeconds = r.seconds;
      return r.shot;
    };
    if (!cur || modeChanged) return want ?? takeRotation(false);
    // Something big is due within the look-ahead: hold this shot rather than cut twice in a row.
    const bigSoon = peek.some((e) => e.type === 'catnip-catch' || e.type === 'phase' || e.type === 'close-finish');
    if (want) {
      if (want.kind === cur.kind && want.subject === cur.subject) return want;
      // The trip, a lost signal, a phase change and a photo finish cut at once; everything else
      // waits for the shot to be readable.
      const urgent = want.priority >= PRIORITY.closeFinish;
      if (want.priority > cur.priority && (held || urgent)) return want;
      if (want.priority <= cur.priority && held && !bigSoon) return want;
      return cur;
    }
    if (cur.priority > PRIORITY.rotation) return held && !bigSoon ? takeRotation(true) : cur;
    if (this.shotTime >= this.rotationSeconds && !bigSoon) return takeRotation(true);
    return cur;
  }

  private remember(s: RaceSnapshot, events: RaceEvent[], peek: RaceEvent[]): void {
    if (s.raceNo !== this.raceNo) {
      this.raceNo = s.raceNo;
      this.trips = []; this.landings = []; this.hamShown.clear();
      this.lastShown = { leadChange: -Infinity, ham: -Infinity };
    }
    for (const e of events) {
      this.recent.push(e);
      if (e.type === 'catnip-catch') {
        const c = s.cats.find((x) => x.id === e.cat);
        this.trips = this.trips.filter((t) => t.cat !== e.cat);
        this.trips.push({ cat: e.cat, lane: c?.lane ?? 0, from: e.at, to: null, catchT: e.t });
      } else if (e.type === 'catnip-landing') {
        const c = s.cats.find((x) => x.id === e.cat);
        this.trips = this.trips.filter((t) => t.cat !== e.cat);
        this.landings.push({ cat: e.cat, lane: c?.lane ?? 0, to: e.to, t: e.t });
      } else if (e.type === 'phase') {
        this.trips = []; this.hamShown.clear();
      }
    }
    // The landing spot is known 2 s ahead: aim the landing shot at it.
    for (const e of peek) {
      if (e.type !== 'catnip-landing') continue;
      const trip = this.trips.find((t) => t.cat === e.cat);
      if (trip) trip.to = e.to;
    }
    // Joined in the middle of a trip: start tracking it from where the cat is.
    for (const c of s.cats) {
      if (c.tripping && !this.trips.some((t) => t.cat === c.id)) {
        this.trips.push({ cat: c.id, lane: c.lane, from: c.signalPos, to: null, catchT: s.t - 2000 });
      }
      if (!c.eating) this.hamShown.delete(c.id);
    }
    this.trips = this.trips.filter((t) => s.t - t.catchT < TRIP_MS + 2500 && s.t >= t.catchT - 1);
    this.landings = this.landings.filter((l) => s.t - l.t < 5000 && s.t >= l.t - 1);
    this.recent = this.recent.filter((e) => s.t - e.t < RECENT_MS && e.t <= s.t + 1);
  }

  private tripInfos(s: RaceSnapshot): TripInfo[] {
    return this.trips.map((t) => ({ cat: t.cat, lane: t.lane, from: t.from, to: t.to, elapsed: Math.max(0, (s.t - t.catchT) / 1000) }));
  }
}

/**
 * How far the finish camera has pushed in on a photo finish: from 0 when the close finish shows up
 * in the 2 s look-ahead to 1 at the crossing, held at 1 after it.
 */
function tightPush(s: RaceSnapshot, peek: RaceEvent[]): number {
  const ahead = peek.find((e) => e.type === 'close-finish');
  if (!ahead) return 1;
  const u = Math.min(1, Math.max(0, 1 - (ahead.t - s.t) / PEEK_MS));
  return u * u * (3 - 2 * u);
}
const sub = (a: Vec3, b: Vec3) => v(a.x - b.x, a.y - b.y, a.z - b.z);
const add = (a: Vec3, b: Vec3) => v(a.x + b.x, a.y + b.y, a.z + b.z);

/** The field of view (vertical, degrees) at least as wide sideways as `fov` shows on a square
 *  screen. Shots are composed for wide screens; on a tall phone screen the same vertical angle
 *  would show only a narrow strip, so the frame widens, adding sky and ground instead. */
export function screenFov(fov: number, aspect: number): number {
  if (aspect >= 1) return fov;
  const half = Math.atan(Math.tan((fov * Math.PI) / 360) / aspect);
  return Math.min(90, (half * 360) / Math.PI);
}

/** Puts a camera state on the three.js camera. */
export function applyCamera(camera: PerspectiveCamera, state: CameraState): void {
  camera.position.set(state.position.x, state.position.y, state.position.z);
  camera.up.set(0, 1, 0);
  camera.lookAt(state.target.x, state.target.y, state.target.z);
  const fov = screenFov(state.fov, camera.aspect);
  if (Math.abs(camera.fov - fov) > 0.01) { camera.fov = fov; camera.updateProjectionMatrix(); }
}
